// ========================================================
// FILE: src/pages/Shop/ShopCart.jsx
// FULLY FIXED SHOP CART
// REMOVE BUTTON FIXED
// ========================================================

import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import {
  Link,
  useNavigate,
} from "react-router-dom";

import ScrollToTop from "../ScrollToTop";

import Swal from "sweetalert2";

import api from "../../utils/api";

import "./ShopCart.css";

const BASE_URL =
  "https://api.freshcart4u.com";

const ShopCart = () => {

  const navigate =
    useNavigate();

  const userId =
    localStorage.getItem(
      "userId"
    );

  const [loading,
    setLoading] =
    useState(true);

  const [cartItems,
    setCartItems] =
    useState([]);

  /* =====================================================
      IMAGE
  ===================================================== */

  const getImage = (
    img
  ) => {

    if (!img)
      return "https://via.placeholder.com/120";

    if (
      img.startsWith(
        "http"
      )
    )
      return img;

    return `${BASE_URL}${img}`;
  };

  /* =====================================================
      FINAL PRICE
  ===================================================== */

  const getFinalPrice =
    (
      p
    ) => {

      if (!p)
        return 0;

      if (
        p.offerType ===
        "PERCENT"
      ) {

        return (
          p.price -
          Math.round(
            (
              p.price *
              p.offerValue
            ) / 100
          )
        );
      }

      if (
        p.offerType ===
        "FLAT"
      ) {

        return (
          p.price -
          p.offerValue
        );
      }

      return p.price;
    };

  /* =====================================================
      FETCH CART
  ===================================================== */

  const fetchCart =
    useCallback(
      async () => {

        if (!userId) {

          setLoading(
            false
          );

          return;
        }

        try {

          const res =
            await api.get(
              `/api/cart/${userId}`
            );

          const items =
            res.data.items || [];

          const merged =
            {};

          items.forEach(
            (item) => {

              const p =
                item.productId ||
                item.product;

              if (p) {

                const pid =
                  p._id || p;

                if (
                  merged[
                    pid
                  ]
                ) {

                  merged[
                    pid
                  ].quantity +=
                    item.quantity;

                } else {

                  merged[
                    pid
                  ] = {
                    ...item,
                    productId: p,
                  };
                }
              }
            }
          );

          setCartItems(
            Object.values(
              merged
            )
          );

        } catch (err) {

          console.log(
            "Cart Fetch Error:",
            err
          );

        } finally {

          setLoading(
            false
          );

        }
      },
      [userId]
    );

  /* =====================================================
      LOAD CART
  ===================================================== */

  useEffect(() => {

    fetchCart();

  }, [fetchCart]);

  /* =====================================================
      AUTO REFRESH
  ===================================================== */

  useEffect(() => {

    const handleCart =
      () => {

        fetchCart();

      };

    window.addEventListener(
      "cartUpdated",
      handleCart
    );

    return () => {

      window.removeEventListener(
        "cartUpdated",
        handleCart
      );
    };

  }, [fetchCart]);

  /* =====================================================
      SAFE CART
  ===================================================== */

  const safeCartItems =
    cartItems.filter(
      (item) =>
        item &&
        (
          item.productId ||
          item.product
        )
    );

  /* =====================================================
      UPDATE QTY
  ===================================================== */

  const updateQuantity =
    async (
      pid,
      qty
    ) => {

      if (
        qty <= 0
      ) {

        removeItem(
          pid
        );

        return;
      }

      setCartItems(
        (prev) =>
          prev.map(
            (item) => {

              const p =
                item.productId ||
                item.product;

              if (
                p?._id ===
                pid
              ) {

                return {
                  ...item,
                  quantity:
                    qty,
                };
              }

              return item;
            }
          )
      );

      try {

        await api.post(
          "/api/cart/update",
          {
            userId,
            productId:
              pid,
            quantity:
              qty,
          }
        );

        window.dispatchEvent(
          new Event(
            "cartUpdated"
          )
        );

      } catch (err) {

        console.log(
          "Update Error:",
          err
        );

        fetchCart();
      }
    };

  /* =====================================================
      REMOVE ITEM
  ===================================================== */

  const removeItem =
    async (
      pid
    ) => {

      try {

        await api.post(
          "/api/cart/remove",
          {
            userId,
            productId:
              pid,
          }
        );

        setCartItems(
          (prev) =>
            prev.filter(
              (item) => {

                const p =
                  item.productId ||
                  item.product;

                return (
                  p?._id !==
                  pid
                );
              }
            )
        );

        window.dispatchEvent(
          new Event(
            "cartUpdated"
          )
        );

        Swal.fire({
          icon: "success",
          title:
            "Item Removed",
          timer: 1000,
          showConfirmButton:
            false,
        });

      } catch (err) {

        console.log(
          "Remove Error:",
          err
        );

        Swal.fire({
          icon: "error",
          title:
            "Remove Failed",
          text:
            "Unable to remove item",
        });
      }
    };

  /* =====================================================
      TOTALS
  ===================================================== */

/* =====================================================
    TOTALS
===================================================== */

const totalAmount =
  safeCartItems.reduce(
    (
      sum,
      item
    ) => {

      const p =
        item.productId ||
        item.product;

      return (
        sum +
        getFinalPrice(p) *
        item.quantity
      );
    },
    0
  );

const totalItems =
  safeCartItems.reduce(
    (
      sum,
      item
    ) =>
      sum +
      item.quantity,
    0
  );


/* =====================================================
    FREE DELIVERY OFFER
    ₹99 OR MORE = FREE DELIVERY UP TO 3 KM
===================================================== */

const FREE_DELIVERY_LIMIT = 99;

const isFreeDelivery =
  totalAmount >= FREE_DELIVERY_LIMIT;

const amountForFreeDelivery =
  Math.max(
    0,
    FREE_DELIVERY_LIMIT - totalAmount
  );



  
  /* =====================================================
      LOADING
  ===================================================== */

  if (loading) {

    return (

      <div className="cart-loader">

        <div className="spinner-border text-success" />

      </div>
    );
  }

  /* =====================================================
      EMPTY CART
  ===================================================== */

  if (
    !safeCartItems.length
  ) {

    return (

      <div className="empty-cart-page">

        <ScrollToTop />

        <img
          src="https://cdn-icons-png.flaticon.com/512/2038/2038854.png"
          alt=""
        />

        <h2>

          Your Cart is Empty 😕

        </h2>

        <p>

          Add some fresh groceries

        </p>

        <Link
          to="/shop"
          className="shop-btn"
        >

          Start Shopping

        </Link>

      </div>
    );
  }

  /* =====================================================
      UI
  ===================================================== */

  return (

    <div className="shop-cart-page">

      <ScrollToTop />

      {/* HEADER */}

      <div className="cart-header">

        <button
          className="back-btn"
          onClick={() =>
            navigate(
              "/shop"
            )
          }
        >

          ←

        </button>

        <div>

          <h2>

            Shopping Cart

          </h2>

          <p>

            {totalItems}
            {" "}
            Items Added

          </p>

        </div>

      </div>

      {/* CART ITEMS */}

      <div className="cart-items">

        {safeCartItems.map(
          (item) => {

            const p =
              item.productId ||
              item.product;

            return (

              <div
                key={
                  item._id
                }
                className="cart-card"
              >

                {/* IMAGE */}

                <div className="cart-image">

                  <img
                    src={getImage(
                      p.images?.[0]
                    )}
                    alt={
                      p.name
                    }
                  />

                </div>

                {/* INFO */}

                <div className="cart-info">

                  <span className="cart-category">

                    {
                      p.category
                    }

                  </span>

                  <h4>

                    {p.name}

                  </h4>

                  <p>

                    {p.description
                      ? p.description.slice(
                          0,
                          70
                        )
                      : "Fresh grocery product"}

                  </p>

                  {/* PRICE + QTY */}

                  <div className="cart-bottom-row">

                    <div>

                      {p.mrp &&
                        p.mrp >
                          getFinalPrice(
                            p
                          ) && (

                          <span className="old-price">

                            ₹
                            {
                              p.mrp
                            }

                          </span>

                        )}

                      <h3>

                        ₹
                        {
                          getFinalPrice(
                            p
                          )
                        }

                      </h3>

                    </div>

                    {/* QTY */}

                    <div className="qty-control">

                      <button
                        onClick={() =>
                          updateQuantity(
                            p._id,
                            item.quantity -
                              1
                          )
                        }
                      >

                        -

                      </button>

                      <span>

                        {
                          item.quantity
                        }

                      </span>

                      <button
                        onClick={() =>
                          updateQuantity(
                            p._id,
                            item.quantity +
                              1
                          )
                        }
                      >

                        +

                      </button>

                    </div>

                  </div>

                  {/* REMOVE */}

                  <button
                    type="button"
                    className="remove-btn"
                    onClick={() =>
                      removeItem(
                        p._id
                      )
                    }
                  >

                    Remove

                  </button>

                </div>

              </div>
            );
          }
        )}

      </div>

      {/* SUMMARY */}

<div className="summary-box">

  <h3>
    Order Summary
  </h3>

  {/* FREE DELIVERY OFFER */}

  {isFreeDelivery ? (

    <div
      style={{
        background: "#e8f8ee",
        border: "1px solid #b7e4c7",
        color: "#198754",
        padding: "12px 14px",
        borderRadius: "12px",
        marginBottom: "15px",
        fontWeight: "600"
      }}
    >
      🎉 FREE DELIVERY UNLOCKED!

      <div
        style={{
          fontSize: "13px",
          fontWeight: "500",
          marginTop: "3px"
        }}
      >
        Free delivery up to 3 KM 🚚
      </div>

    </div>

  ) : (

    <div
      style={{
        background: "#fff8e1",
        border: "1px solid #ffe082",
        color: "#856404",
        padding: "12px 14px",
        borderRadius: "12px",
        marginBottom: "15px",
        fontWeight: "600"
      }}
    >
      🚚 Add ₹{amountForFreeDelivery} more

      <div
        style={{
          fontSize: "13px",
          fontWeight: "500",
          marginTop: "3px"
        }}
      >
        and get FREE DELIVERY up to 3 KM 🎉
      </div>

    </div>

  )}
      

        <div className="summary-row">

          <span>

            Total Items

          </span>

          <strong>

            {
              totalItems
            }

          </strong>

        </div>
<div className="summary-row">

  <span>
    Delivery Fee
  </span>

  {isFreeDelivery ? (

    <strong className="free-text">
      FREE 🎉
    </strong>

  ) : (

    <strong>
      Calculated at Checkout
    </strong>

  )}

</div>
        

        <div className="summary-row total-row">

          <span>

            Total Amount

          </span>

          <strong>

            ₹
            {
              totalAmount
            }

          </strong>

        </div>

        <button
          className="checkout-btn"
          onClick={() =>
            navigate(
              "/checkout"
            )
          }
        >

          Proceed to Checkout

        </button>

      </div>

    </div>
  );
};

export default ShopCart;
